import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { Center, Loader, Alert } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { fetchPublicVenueProfile, type PublicVenueProfile } from './api-client';

/**
 * The worker serves one venue profile — that profile is the script for this
 * deployment. The portal fetches it once at boot and everything downstream
 * reads from it: venueId, branding, capability flags for conditional render.
 *
 * No fallback profile is ever synthesised: if the fetch fails, the UI stays
 * on an error state rather than showing stale/generic data. UEEDVP §5.
 */
const ProfileContext = createContext<PublicVenueProfile | null>(null);

export function ProfileProvider({ children }: { children: ReactNode }): JSX.Element {
  const [profile, setProfile] = useState<PublicVenueProfile | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchPublicVenueProfile()
      .then(setProfile)
      .catch((e: Error) => setError(e.message));
  }, []);

  if (error) {
    return (
      <Center py="xl">
        <Alert icon={<IconAlertCircle size={16} />} title="無法載入場地設定" color="red">
          {error}
        </Alert>
      </Center>
    );
  }

  if (!profile) {
    return (
      <Center py="xl">
        <Loader size="lg" />
      </Center>
    );
  }

  return <ProfileContext.Provider value={profile}>{children}</ProfileContext.Provider>;
}

/**
 * Access the active venue profile. Must be used inside a <ProfileProvider />.
 * Throws if called before the profile has loaded — callers that need the
 * loading state should render inside ProfileProvider which handles that.
 */
export function useVenueProfile(): PublicVenueProfile {
  const profile = useContext(ProfileContext);
  if (!profile) {
    throw new Error('useVenueProfile must be used inside <ProfileProvider />');
  }
  return profile;
}
